import { useCallback, useEffect, useRef, useState } from "react";
import { useRoomContext, type TrackReferenceOrPlaceholder } from "@livekit/components-react";
import { facingModeFromLocalTrack, Track } from "livekit-client";
import { useMutation } from "@tanstack/react-query";

import { useRoomSync } from "../RoomSyncProvider";
import type { SyncRoomStateView } from "../syncApi";
import { captureFromTrack, captureFromVideoElement, FrameCaptureError, type CapturedFrame } from "./captureFrame";

/**
 * useFreezeController - the shared freeze on the client (D-07, RS-11). Runs on both sides:
 *
 * - Owner responder (worker and customer alike): whichever participant is named as the source in
 *   `sync.room.freezeRequest` captures its own track once and delivers the canonical frame.
 * - Worker freeze/resumeLive: only the worker role can start a freeze or resume live. If the
 *   named source does not deliver within 5s (for example a customer still on the old room, with
 *   no sync layer at all), the worker falls back to capturing its own rendered stage video, so a
 *   freeze always completes.
 *
 * Must be used inside RoomSyncProvider and LiveKitRoom (both `useRoomSync` and `useRoomContext`
 * throw/require their provider otherwise).
 */

export type FreezeStatus = "idle" | "requesting" | "waiting-for-source" | "capturing" | "uploading" | "frozen" | "error";

export type FreezeController = {
  status: FreezeStatus;
  error: string | null;
  canFreeze: (track?: TrackReferenceOrPlaceholder) => boolean;
  freeze: (track: TrackReferenceOrPlaceholder) => void;
  resumeLive: () => void;
};

const OWNER_REQUEST_MAX_AGE_MS = 55_000;
const WORKER_FALLBACK_TIMEOUT_MS = 5000;
const SOURCE_CAPTURE_ERROR = "The source could not be captured.";
const SHARE_ERROR = "Could not share the frozen frame.";
const RESUME_ERROR = "Could not resume live.";
const DELIVERY_GRACE_MS = 2000;
// Caps handledFrameIdsRef so it cannot grow unbounded for the lifetime of a very long call (IN-01).
const MAX_HANDLED_FRAME_IDS = 50;

const BUSY_STATUSES: ReadonlySet<FreezeStatus> = new Set(["requesting", "waiting-for-source", "capturing", "uploading"]);

export function useFreezeController(p: { getStageVideoElement: () => HTMLVideoElement | null }) {
  const room = useRoomContext();
  const sync = useRoomSync();

  const [phase, setPhase] = useState<FreezeStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  // Frame ids this participant has already captured for (owner responder), so a re-broadcast of
  // the same freezeRequest is never captured twice (T-05-75).
  const handledFrameIdsRef = useRef<Set<string>>(new Set());
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFallbackFrameIdRef = useRef<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (input: { frameId: string; trackSid: string; captured: CapturedFrame; mirrored: boolean }) => {
      return sync.api.uploadFrame(
        {
          frameId: input.frameId,
          trackSid: input.trackSid,
          width: input.captured.width,
          height: input.captured.height,
          mirrored: input.mirrored,
          rotation: 0,
          capturedAtEpochMs: Date.now(),
        },
        new File([input.captured.blob], `frame-${input.frameId}.jpg`, { type: "image/jpeg" }),
      );
    },
  });

  const requestFreezeMutation = useMutation({
    mutationFn: (input: { trackSid: string; identity: string }) => {
      const worker = sync.api.worker;
      if (!worker) {
        return Promise.reject(new Error("Only the worker can start a freeze."));
      }
      return worker.requestFreeze(input);
    },
  });

  const resumeLiveMutation = useMutation({
    mutationFn: () => {
      const worker = sync.api.worker;
      if (!worker) {
        return Promise.reject(new Error("Only the worker can resume live."));
      }
      return worker.resumeLive();
    },
  });

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current != null) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    pendingFallbackFrameIdRef.current = null;
  }, []);

  // The fallback timer only needs to reach the frame it started waiting for, never one delivered
  // by a later freeze - clear it the moment that exact frame becomes the active one.
  useEffect(() => {
    const activeFrameId = sync.room?.activeFrame?.frameId ?? null;
    if (activeFrameId !== null && activeFrameId === pendingFallbackFrameIdRef.current) {
      clearFallbackTimer();
    }
  }, [sync.room?.activeFrame?.frameId, clearFallbackTimer]);

  useEffect(() => clearFallbackTimer, [clearFallbackTimer]);

  /**
   * Delivers a captured frame the way D-07 requires: the fast path over the data channel first
   * (the owner sees it at once), then the durable POST. Undelivered errors (the request expired,
   * or someone else - the worker fallback - already delivered this frame id) only surface once no
   * active frame for this id arrives within a short grace period (T-05-75).
   */
  const deliver = useCallback(
    async (frameId: string, trackSid: string, captured: CapturedFrame, mirrored: boolean) => {
      sync.registerLocalFrameImage(frameId, captured.blob);
      void sync.sendFrameBytes(frameId, new Uint8Array(await captured.blob.arrayBuffer()));
      setPhase("uploading");
      try {
        await uploadMutation.mutateAsync({ frameId, trackSid, captured, mirrored });
        sync.refresh();
        setPhase("idle");
      } catch {
        setTimeout(() => {
          if (sync.room?.activeFrame?.frameId !== frameId) {
            setPhase("error");
            setError(SHARE_ERROR);
          } else {
            setPhase("idle");
          }
        }, DELIVERY_GRACE_MS);
      }
    },
    [sync, uploadMutation],
  );

  // Owner responder: whichever side (worker or customer) is named as the source captures and
  // delivers, only ever answering a request that names its own identity (T-05-72).
  useEffect(() => {
    const request = sync.room?.freezeRequest;
    if (!request || request.identity !== sync.localIdentity) {
      return;
    }
    if (handledFrameIdsRef.current.has(request.frameId)) {
      return;
    }
    const ageMs = Date.now() - request.requestedAtEpochMs;
    if (ageMs >= OWNER_REQUEST_MAX_AGE_MS) {
      return;
    }
    handledFrameIdsRef.current.add(request.frameId);
    // Keep only the most recent MAX_HANDLED_FRAME_IDS entries (a Set iterates in insertion order,
    // so the first key is the oldest).
    while (handledFrameIdsRef.current.size > MAX_HANDLED_FRAME_IDS) {
      const oldest = handledFrameIdsRef.current.keys().next().value;
      if (oldest === undefined) {
        break;
      }
      handledFrameIdsRef.current.delete(oldest);
    }

    const publication = room.localParticipant.trackPublications.get(request.trackSid);
    const track = publication?.track;
    if (!track) {
      // No local publication for this trackSid - the worker fallback covers it.
      return;
    }
    const mirrored = track.source === Track.Source.Camera && facingModeFromLocalTrack(track).facingMode === "user";

    setPhase("capturing");
    setError(null);
    void (async () => {
      try {
        const captured = await captureFromTrack(track.mediaStreamTrack, track.attachedElements as HTMLVideoElement[]);
        await deliver(request.frameId, request.trackSid, captured, mirrored);
      } catch (err) {
        setPhase("error");
        setError(err instanceof FrameCaptureError ? err.message : SOURCE_CAPTURE_ERROR);
      }
    })();
  }, [sync.room?.freezeRequest, sync.localIdentity, room, deliver]);

  /** Worker only (no-op for the customer role): starts a freeze naming `track`'s source. */
  const freeze = useCallback(
    (track: TrackReferenceOrPlaceholder) => {
      const worker = sync.api.worker;
      if (!worker) {
        return;
      }
      const trackSid = track.publication?.trackSid;
      if (!trackSid) {
        return;
      }
      const identity = track.participant.identity;

      clearFallbackTimer();
      setPhase("requesting");
      setError(null);

      requestFreezeMutation.mutate(
        { trackSid, identity },
        {
          onSuccess: (view: SyncRoomStateView) => {
            sync.applyServerState(view);
            const request = view.freezeRequest;
            if (!request) {
              return;
            }
            if (request.identity === sync.localIdentity) {
              // The owner responder effect above already picks this up.
              return;
            }
            setPhase("waiting-for-source");
            pendingFallbackFrameIdRef.current = request.frameId;
            fallbackTimerRef.current = setTimeout(() => {
              fallbackTimerRef.current = null;
              if (sync.room?.activeFrame?.frameId === request.frameId) {
                return;
              }
              const videoElement = p.getStageVideoElement();
              if (!videoElement) {
                setPhase("error");
                setError(SOURCE_CAPTURE_ERROR);
                return;
              }
              setPhase("capturing");
              void (async () => {
                try {
                  const captured = await captureFromVideoElement(videoElement);
                  await deliver(request.frameId, request.trackSid, captured, false);
                } catch {
                  setPhase("error");
                  setError(SOURCE_CAPTURE_ERROR);
                }
              })();
            }, WORKER_FALLBACK_TIMEOUT_MS);
          },
          onError: () => {
            setPhase("error");
            setError(SOURCE_CAPTURE_ERROR);
          },
        },
      );
    },
    [sync, requestFreezeMutation, deliver, clearFallbackTimer, p],
  );

  /** Worker only (no-op for the customer role): clears the active frame for everyone (D-07). */
  const resumeLive = useCallback(() => {
    const worker = sync.api.worker;
    if (!worker) {
      return;
    }
    clearFallbackTimer();
    resumeLiveMutation.mutate(undefined, {
      onSuccess: (view: SyncRoomStateView) => {
        sync.applyServerState(view);
        setPhase("idle");
        setError(null);
      },
      onError: () => {
        setPhase("error");
        setError(RESUME_ERROR);
      },
    });
  }, [sync, resumeLiveMutation, clearFallbackTimer]);

  const canFreeze = useCallback(
    (track?: TrackReferenceOrPlaceholder) => {
      if (!sync.api.worker) {
        return false;
      }
      if (!track?.publication?.trackSid) {
        return false;
      }
      return !BUSY_STATUSES.has(phase);
    },
    [sync.api.worker, phase],
  );

  // Displayed status: a request in flight always wins; otherwise a stored active frame reads as
  // "frozen" even though this hook's own phase has settled back to idle.
  const status: FreezeStatus = BUSY_STATUSES.has(phase) ? phase : sync.room?.activeFrame != null ? "frozen" : phase;

  return { status, error, canFreeze, freeze, resumeLive };
}

export default useFreezeController;
